import { useState, useEffect, useRef } from 'react';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from '../lib/utils';
import { Button } from './button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from './dropdown-menu';

export type SingleSelectDropdownProps<T> = {
  items: T[];
  selectedItem: T;
  onChange: (item: T) => void;
  getItemLabel: (item: T) => string;
  getItemValue: (item: T) => string;
  placeholder?: string;
  buttonClassName?: string;
  noItemsFoundLabel?: string;
};

export const SingleSelectDropdown = <T,>({
  items,
  selectedItem,
  onChange,
  getItemLabel,
  getItemValue,
  placeholder = 'Select an item',
  buttonClassName,
  noItemsFoundLabel = 'No items found',
}: SingleSelectDropdownProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [minWidth, setMinWidth] = useState<number | undefined>(undefined);
  const measureRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!measureRef.current) return;
    // Find the widest label
    let maxWidth = 0;
    const children = Array.from(measureRef.current.children) as HTMLDivElement[];
    children.forEach((child) => {
      const width = child.offsetWidth;
      if (width > maxWidth) maxWidth = width;
    });
    // Add extra for icon and padding (e.g., 40px)
    setMinWidth(maxWidth + 40);
  }, [items, getItemLabel]);

  return (
    <>
      {/* Hidden element to measure label widths */}
      <div
        ref={measureRef}
        style={{ position: 'absolute', visibility: 'hidden', height: 0, overflow: 'hidden', whiteSpace: 'nowrap' }}
        aria-hidden="true"
      >
        {items.map((item) => (
          <span key={getItemValue(item)} className="vai-font-normal vai-text-left vai-px-4 vai-py-2">
            {getItemLabel(item)}
          </span>
        ))}
        {/* Also measure placeholder if no selection */}
        {!selectedItem && (
          <span className="vai-font-normal vai-text-left vai-px-4 vai-py-2">{placeholder}</span>
        )}
      </div>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'vai-justify-between vai-text-left vai-font-normal',
              buttonClassName,
            )}
            style={minWidth ? { minWidth } : undefined}
          >
            {selectedItem ? getItemLabel(selectedItem) : placeholder}
            <FontAwesomeIcon icon={faAngleDown} className="vai-ml-2" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="vai-w-[--radix-popper-anchor-width] vai-max-h-[65vh] vai-overflow-y-auto"
        >
          {!items.length && (
            <DropdownMenuLabel>{noItemsFoundLabel}</DropdownMenuLabel>
          )}
          <div className="vai-flex vai-flex-col vai-justify-start">
            {items.map((item) => (
              <Button
                key={getItemValue(item)}
                variant="ghost"
                className="vai-w-full !vai-justify-start vai-px-4 vai-py-2 vai-text-left vai-font-normal hover:vai-bg-slate-50"
                onClick={() => {
                  onChange(item);
                  setIsOpen(false);
                }}
              >
                <div className="vai-flex vai-items-start vai-gap-3">
                  <div
                    className={cn(
                      'vai-h-4 vai-w-4 vai-rounded-full vai-border vai-flex vai-items-center vai-justify-center vai-flex-shrink-0',
                      getItemValue(selectedItem) === getItemValue(item)
                        ? 'vai-bg-blue-500 vai-border-blue-500'
                        : 'vai-border-gray-300',
                    )}
                  >
                    {getItemValue(selectedItem) === getItemValue(item) && (
                      <div className="vai-h-2 vai-w-2 vai-rounded-full vai-bg-white" />
                    )}
                  </div>
                  <span className="vai-truncate vai-font-semibold vai-text-left">
                    {getItemLabel(item)}
                  </span>
                </div>
              </Button>
            ))}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}; 